import { useTranslation } from 'react-i18next'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

// Signed, and fixed to the two decimals products.weight stores. A bare
// difference like "0.14000000000000012" is float noise, and an unsigned one
// leaves the clerk working out which side of their reading the piece falls.
export function formatWeightDelta(weight, searched) {
    if (weight === null || weight === undefined) return null
    if (searched === null || searched === undefined || searched === '') return null

    const difference = Number(weight) - Number(searched)
    if (!Number.isFinite(difference)) return null

    return `${difference > 0 ? '+' : difference < 0 ? '-' : ''}${Math.abs(difference).toFixed(2)}`
}

// Newark, Long Branch, both -- or neither, for a match the server only
// surfaced because "search everything, not just what's in stock" was on.
function storeLabel(match, t) {
    const atNewark = Number(match.newarkQuantity) > 0
    const atLongBranch = Number(match.longbranchQuantity) > 0
    if (atNewark && atLongBranch) return `${t('findPiece.atNewark')} · ${t('findPiece.atLongBranch')}`
    if (atNewark) return t('findPiece.atNewark')
    if (atLongBranch) return t('findPiece.atLongBranch')
    return null
}

// Archived/draft is the one piece of state on the tile that IS a status, so it
// alone gets the 999px pill -- theme.js reserves that shape for status, and a
// weight delta is a measurement, not one.
function statusLabel(match, t) {
    if (match.status === 'archived') return t('findPiece.archived')
    if (match.status === 'draft') return t('findPiece.draft')
    return null
}

// The photo is the tile: square, edge to edge, metadata in a compact block
// beneath it. A piece with no photo (1,382 of 1,383 in-stock pieces have one)
// renders as a plate rather than crashing the grid it sits in.
function Tile(props) {
    const { match, searchedWeight, onSelect, onImageClick } = props
    const { t } = useTranslation()

    const delta = formatWeightDelta(match.weight, searchedWeight)
    const store = storeLabel(match, t)
    const status = statusLabel(match, t)
    const weightText = Number.isFinite(Number(match.weight)) && match.weight !== null && match.weight !== undefined
        ? `${Number(match.weight).toFixed(2)} g`
        : null
    const priceText = match.price !== null && match.price !== undefined
        ? `$${Number(match.price).toLocaleString('en-US')}`
        : null
    const alt = [match.category, match.metal].filter(Boolean).join(', ')

    return (
        <Box
            data-testid={`find-piece-tile-${match.barcode}`}
            onClick={() => onSelect(match)}
            sx={{
                bgcolor: 'background.paper',
                border: 1,
                borderColor: 'divider',
                borderRadius: '12px',
                overflow: 'hidden',
                cursor: 'pointer'
            }}
        >
            <Box sx={{ position: 'relative', aspectRatio: '1', bgcolor: 'action.hover', display: 'block' }}>
                {match.imageUrl ? (
                    <Box
                        component='img'
                        src={match.imageUrl}
                        alt={alt}
                        onClick={event => {
                            // The sheet would cover the picture it just opened -- the
                            // same rule Products.js applies to its own thumbnails.
                            event.stopPropagation()
                            onImageClick(match.imageUrl)
                        }}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                ) : (
                    <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
                        {/* A tone of its own, lighter than text.secondary -- the
                            approved mock's .nophoto sits on the #F1F1F4 plate, not
                            on the surface, and needs to read as a hint, not a label. */}
                        <Typography variant='caption' sx={{ color: '#B4B4C0' }}>
                            {t('products.noImage')}
                        </Typography>
                    </Box>
                )}
                {status && (
                    <Box
                        sx={{
                            position: 'absolute', top: 8, left: 8, borderRadius: 999,
                            px: 1.125, py: 0.375, fontSize: '0.6875rem', fontWeight: 600,
                            letterSpacing: '0.02em', bgcolor: 'rgba(25, 25, 33, 0.82)', color: '#fff'
                        }}
                    >
                        {status}
                    </Box>
                )}
            </Box>

            <Box sx={{ px: 1.5, pt: 1.25, pb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.875 }}>
                    {weightText && (
                        <Typography
                            component='span'
                            sx={{ fontSize: '1.0625rem', fontWeight: 600, letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums' }}
                        >
                            {weightText}
                        </Typography>
                    )}
                    {delta && (
                        <Typography component='span' sx={{ fontSize: '0.8125rem', color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                            {delta}
                        </Typography>
                    )}
                </Box>
                <Box sx={{ mt: 0.375, display: 'flex', flexWrap: 'wrap', columnGap: 1, rowGap: 0.5 }}>
                    {priceText && (
                        <Typography component='span' sx={{ fontSize: '0.8125rem', color: 'text.primary', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
                            {priceText}
                        </Typography>
                    )}
                    {store && (
                        <Typography component='span' sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
                            {/* .store::before in the approved mock: a quiet middle-dot
                                separator, only ever present alongside a price. */}
                            <Box component='span' sx={{ color: '#C2C2CC', mr: 1 }}>·</Box>
                            {store}
                        </Typography>
                    )}
                </Box>
            </Box>
        </Box>
    )
}

// Two columns at 390px, four at 1280px -- both approved widths. `sm` (600px)
// is also where the sticky 336px filter panel unfolds inline (FindPiece.js's
// useIsMobile boundary), so a four-column step there shares the row with the
// panel and leaves tiles ~80px wide between 600 and ~900px, a width nobody
// reviewed. `md` (900px) keeps both approved widths unchanged and behaves in
// between.
export default function FindPieceResults(props) {
    const { matches, searchedWeight, onSelect, onImageClick } = props

    return (
        <Box
            data-testid='find-piece-results-grid'
            sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
                gap: { xs: 1.75, sm: 2.25 }
            }}
        >
            {matches.map(match => (
                <Tile
                    key={match.barcode}
                    match={match}
                    searchedWeight={searchedWeight}
                    onSelect={onSelect}
                    onImageClick={onImageClick}
                />
            ))}
        </Box>
    )
}
